import { Timestamp } from 'firebase/firestore';

export type AttendanceStatus = 'present' | 'absent' | 'late' | 'holiday' | 'pending';

export type AbsentReason = 
  | 'sick_leave'
  | 'personal_emergency'
  | 'planned_leave'
  | 'family_emergency'
  | 'other';

export type ManualCheckInReason =
  | 'network_issue'
  | 'gps_error'
  | 'working_remotely'
  | 'field_work'
  | 'other';

export interface GeoLocation {
  latitude: number;
  longitude: number;
  accuracy?: number;
  timestamp?: number;
}

export interface LocationCheckResult {
  isWithinRange: boolean;
  distance: number; // in meters
  officeLocation: GeoLocation;
  userLocation: GeoLocation;
  allowedRadius: number; // in meters
}

export interface CheckInData {
  userId: string;
  userName: string;
  date: string; // YYYY-MM-DD format
  checkInTime: Date;
  location: GeoLocation;
  officeId: string;
  officeName: string;
  isWithinRange: boolean;
  distance: number; // Distance from office in meters
  isLate: boolean;
  lateReason?: string; // Reason if late (after shift start time)
  locationReason?: string; // Reason if outside 200m range
  manualCheckIn?: boolean; // True if manually checked in due to location issues
  manualCheckInReason?: ManualCheckInReason;
  manualCheckInRemarks?: string;
}

export interface CheckOutData {
  checkOutTime: Date;
  checkOutLocation: GeoLocation;
  isWithinRange: boolean;
  distance: number;
}

export interface AttendanceRecord {
  id: string;
  userId: string;
  userName: string;
  date: string; // YYYY-MM-DD format
  officeId: string;
  officeName: string;
  status: AttendanceStatus;
  
  // Check-in details
  checkInTime?: Date | Timestamp;
  checkInLocation?: GeoLocation;
  checkInWithinRange?: boolean;
  checkInDistance?: number;
  isLate?: boolean;
  lateReason?: string; // Reason for late arrival (after shift start time)
  locationReason?: string; // Reason for being outside 200m range
  manualCheckIn?: boolean;
  manualCheckInReason?: ManualCheckInReason;
  manualCheckInRemarks?: string;
  
  // Check-out details
  checkOutTime?: Date | Timestamp;
  checkOutLocation?: GeoLocation;
  checkOutWithinRange?: boolean;
  checkOutDistance?: number;
  
  // Absent details
  absentReason?: AbsentReason;
  absentRemarks?: string;
  
  // Metadata
  createdAt?: Date | Timestamp | any; // any allows serverTimestamp()
  updatedAt?: Date | Timestamp | any; // any allows serverTimestamp()
}

export interface AttendanceSettings {
  officeRadius: number; // Default: 100 meters
  lateThresholdMinutes: number; // Default: 15 minutes after shift start
  shiftStartTime: string; // Format: "HH:mm" (e.g., "09:00")
  shiftEndTime: string; // Format: "HH:mm" (e.g., "18:00")
  gracePeriodMinutes: number; // Default: 5 minutes
}

export const DEFAULT_ATTENDANCE_SETTINGS: AttendanceSettings = {
  officeRadius: 200, // 200 meters as per requirement
  lateThresholdMinutes: 0, // No grace period - late after shift start time
  shiftStartTime: "10:35", // Shift start time (can be configured)
  shiftEndTime: "18:00",
  gracePeriodMinutes: 0, // No grace period
};

export const ABSENT_REASON_LABELS: Record<AbsentReason, string> = {
  sick_leave: 'Sick Leave',
  personal_emergency: 'Personal Emergency',
  planned_leave: 'Planned Leave',
  family_emergency: 'Family Emergency',
  other: 'Other',
};

export const MANUAL_CHECKIN_REASON_LABELS: Record<ManualCheckInReason, string> = {
  network_issue: 'Network Issue',
  gps_error: 'GPS Error',
  working_remotely: 'Working Remotely',
  field_work: 'Field Work',
  other: 'Other',
};
